import classNames from 'classnames'
import type { JSX } from 'preact'
import { forwardRef } from 'preact/compat'
import styles from './index.module.scss'

type BaseButtonProps = {
  id?: string
  class?: string
  shape?: 'sqr' | 'rect'
  disabled?: boolean
  type?: 'primary' | 'secondary' | 'danger'
  size?: 's' | 'm'
  role?: JSX.IntrinsicElements['button']['role']
  tabIndex?: number
  onBlur?: () => void
  onClick?: () => void
  onKeyDown?: (e: KeyboardEvent) => void
  onPress?: () => void
  children?: preact.ComponentChildren
}

type ButtonAsButton = BaseButtonProps & {
  href?: never
  target?: never
}

type ButtonAsLink = BaseButtonProps & {
  href: string
  target?: string
  rel?: string
}

type ButtonProps = ButtonAsButton | ButtonAsLink

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (props, ref) => {
    const className = classNames(
      styles.root,
      styles[props.type ?? 'secondary'],
      styles[props.size ?? 'm'],
      styles[props.shape ?? 'rect'],
      props.class,
    )

    const commonProps = {
      id: props.id,
      role: props.role,
      tabIndex: props.tabIndex,
      onBlur: props.onBlur,
      onClick: props.onClick,
      onKeyDown: props.onKeyDown,
      onPointerDown: props.onPress,
      class: className,
    }

    if (props.href) {
      return (
        <a
          {...commonProps}
          ref={ref as preact.Ref<HTMLAnchorElement>}
          href={props.href}
          target={props.target}
          rel={props.rel}
        >
          {props.children}
        </a>
      )
    }

    return (
      <button
        {...commonProps}
        ref={ref as preact.Ref<HTMLButtonElement>}
        disabled={props.disabled}
      >
        {props.children}
      </button>
    )
  },
)
